// @ts-strict-ignore
import {reporter} from 'common/report/error-reporting';

export class Persistence<ObjectType = Record<string, any>> {
  static createNamespace<T = Record<string, any>>(...keys) {
    return new Persistence<T>(keys.join(`:`));
  }

  static createCachedNamespace<T = Record<string, any>>(...keys) {
    const namespace = keys.join(`:`);

    if (!window[`persistenceMemoryCache`]) {
      window[`persistenceMemoryCache`] = {};
    }

    if (!window[`persistenceMemoryCache`][namespace]) {
      window[`persistenceMemoryCache`][namespace] = new Map();
    }

    return new Persistence<T>(namespace, window[`persistenceMemoryCache`][namespace]);
  }

  /** Reset for tests. Pairs well with window.localStorage.clear() */
  static nukeWindowCaches() {
    window[`persistenceMemoryCache`] = undefined;
  }

  public declare namespace: string;
  public declare cache: Map<string, ObjectType>;

  constructor(namespace, cache = new Map()) {
    this.namespace = namespace;
    this.cache = cache;
  }

  public get(key): string | null {
    try {
      return window.localStorage.getItem(this.keyFor(key));
    } catch (err) {
      reporter.error(err);
      return null;
    }
  }

  public set(key, val) {
    try {
      window.localStorage.setItem(this.keyFor(key), val);
    } catch (err) {
      reporter.error(err);
    }
  }

  public getObject(key): ObjectType | null {
    try {
      const localKey = this.keyFor(key);
      let value;

      if (this.cache.has(localKey)) {
        value = this.cache.get(localKey);
      } else {
        const itemValue = window.localStorage.getItem(localKey);
        if (itemValue) {
          value = JSON.parse(itemValue);
        } else {
          value = null;
        }
      }

      // Load into cache as object
      this.cache.set(localKey, value);

      return value;
    } catch (err) {
      reporter.error(err);
      return null;
    }
  }

  public setObject(key, val: ObjectType) {
    try {
      const localKey = this.keyFor(key);
      window.localStorage.setItem(localKey, JSON.stringify(val));
      this.cache.set(localKey, val);
    } catch (err) {
      reporter.error(err);
    }
  }

  public remove(key) {
    try {
      const localKey = this.keyFor(key);
      window.localStorage.removeItem(localKey);
      this.cache.delete(localKey);
    } catch (err) {
      reporter.error(err);
    }
  }

  public clearCachedNamespace() {
    window[`persistenceMemoryCache`][this.namespace] = null;
  }

  public clear(key) {
    window.localStorage.removeItem(this.keyFor(key));
  }

  private keyFor(key) {
    return `${this.namespace}:${key}`;
  }

  public static purge(namespaces: string[]) {
    for (const key of Object.keys(window.localStorage)) {
      for (const namespace of namespaces) {
        if (key.startsWith(`${namespace}:`)) {
          window.localStorage.removeItem(key);
        }
      }
    }
  }
}

interface UserPersistenceEntry<T> {
  value: T;
  expiry: number | null;
}
/**
 * Basic wrapper around persistence that segments namespace by user
 * and supports a TTL to prevent stale data.
 *
 * @deprecated Use Panel Contexts paired with UserLocalStorageHelper
 */
export class UserPersistence<T> {
  private declare persistence: Persistence<UserPersistenceEntry<T>>;
  private declare ttlMs?: number;
  public static BASE_KEY = `$mp_up`;

  constructor({namespace, userId, ttlMs}: {namespace: string; userId?: number | string; ttlMs?: number}) {
    this.persistence = Persistence.createNamespace(UserPersistence.BASE_KEY, userId ?? `anon`, namespace);
    this.ttlMs = ttlMs;
  }

  public set(key, value: T) {
    const expiry =
      typeof this.ttlMs !== `undefined` && Number.isInteger(this.ttlMs) ? new Date().getTime() + this.ttlMs : null;
    this.persistence.setObject(key, {value, expiry});
  }

  public get(key): T | null {
    const storedResult = this.persistence.getObject(key);

    if (storedResult?.expiry && storedResult.expiry < new Date().getTime()) {
      this.persistence.remove(key);
      return null;
    }

    return storedResult?.value ?? null;
  }

  public remove(key) {
    this.persistence.remove(key);
  }

  public static purge() {
    Persistence.purge([UserPersistence.BASE_KEY]);
  }
}
